import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Input } from 'design-system-web';
import Toggle from 'components/form/Toggle';
import Dropdown from 'components/form/Dropdown';
import Label from 'components/general/Label';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import { CustomFieldRenderer } from '../../../../feature/components/CustomFieldsSection/CustomFieldRenderer';
import type { CustomFieldRendererField } from '../../../../feature/components/CustomFieldsSection/customField.types';
import {
  VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS,
  isVisitorCustomFieldSelectType,
} from '../../../../feature/components/CustomFieldsSection/customField.constants';

import modalStyles from './CustomFieldModal.scss';
import styles from './CustomFieldDetailView.scss';
import type { CustomFieldDetailModel } from './types';
import { useVisitorCustomFieldsAdminUIEnabled } from './useVisitorCustomFieldsAdminUIEnabled';

type CustomFieldDetailViewProps = {
  config: CustomFieldDetailModel;
  onCancel: () => void;
  onToggleStatus: (row: CustomFieldDetailModel) => Promise<boolean>;
  isStatusUpdating?: boolean;
};

const REQUIRED_ASTERISK = '*';

function toRendererField(config: CustomFieldDetailModel): CustomFieldRendererField {
  return {
    id: config.id,
    organizationId: config.organizationId,
    fieldKey: config.fieldKey,
    fieldName: config.fieldName,
    fieldType: config.fieldType,
    isEnabled: config.isEnabled,
    options: config.options ?? null,
    placeholder: config.placeholder ?? undefined,
    validationRegex: config.validationRegex ?? undefined,
    createdAtDatetime: config.createdAtDatetime ?? undefined,
    updatedAtDatetime: config.updatedAtDatetime ?? undefined,
    createdByUserId: '',
    updatedByUserId: '',
  };
}

export function CustomFieldDetailView({
  config,
  onCancel,
  onToggleStatus,
  isStatusUpdating = false,
}: CustomFieldDetailViewProps): React.ReactElement | null {
  const customFieldsAdminUiEnabled = useVisitorCustomFieldsAdminUIEnabled();
  const { t } = useTranslation();

  const isSelectType = isVisitorCustomFieldSelectType(config.fieldType);

  const fieldTypeItems = useMemo(
    () =>
      VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS.map(ft => ({
        value: ft.value,
        label: t(ft.labelKey),
      })),
    [t]
  );

  const previewField = useMemo(() => toRendererField(config), [config]);
  const [previewValue, setPreviewValue] = useState<string | string[]>('');

  useEffect(() => {
    setPreviewValue('');
  }, [config]);

  if (!customFieldsAdminUiEnabled) {
    return null;
  }

  return (
    <div className={modalStyles.modalRoot} data-test="custom-field-detail-view">
      <div className={modalStyles.entry}>
        <div className={styles.disableToggleRow}>
          <Toggle
            value={config.isEnabled}
            text={
              config.isEnabled
                ? t('web.admin.visitorManagement.customField.status.enabled')
                : t('web.admin.visitorManagement.customField.status.disabled')
            }
            onChange={() => { onToggleStatus(config); }}
            disabled={isStatusUpdating}
            testId="custom-field-detail-status-toggle"
          />
        </div>

        <fieldset className={modalStyles.field} data-test="custom-field-detail-input-type">
          <Label h1>
            {t('web.admin.content.friendlyProperty.labels.inputType')}
          </Label>
          <div className={styles.dropdownViewLock}>
            <Dropdown
              className={modalStyles.fieldTypeDropdown}
              placeholder={t('web.admin.content.metatag.edit.fieldType.fieldTypeInputPlaceholder')}
              items={fieldTypeItems}
              value={config.fieldType}
              onValueChange={() => undefined}
              doTranslation={false}
              disabled
            />
          </div>
        </fieldset>

        <fieldset className={modalStyles.field} data-test="custom-field-detail-name">
          <Label h2 TooltipComponent={t('web.admin.visitorManagement.customField.form.name.tooltip')}>
            {t('web.admin.content.friendlyProperty.labels.name')}
            <span className={modalStyles.required}>{REQUIRED_ASTERISK}</span>
          </Label>
          <Input
            value={config.fieldName}
            onChange={() => null}
            showClear={false}
            disabled
            placeholder={t('web.admin.content.friendlyProperty.placeholder.name')}
            aria-label={t('web.admin.content.friendlyProperty.labels.name')}
          />
        </fieldset>

        {isSelectType && (
          <fieldset className={modalStyles.field} data-test="custom-field-detail-options">
            <Label h2 TooltipComponent={t('web.admin.visitorManagement.customField.modal.options.description')}>
              {t('web.admin.content.friendlyProperty.labels.options')}
            </Label>
            <ul className={modalStyles.options}>
              {(config.options ?? []).map((option, index) => (
                <li key={`${option.value}-${index}`} className={modalStyles.optionRow}>
                  <span
                    className={modalStyles.iconReorder}
                    title={t('web.admin.visitorManagement.customField.form.options.dragReorderTooltip')}
                    aria-hidden
                  >
                    <Icon name="bars" set={ICON_SET_FONTAWESOME} />
                  </span>
                  <div className={modalStyles.optionInputGroup}>
                    <Input
                      className={modalStyles.optionInput}
                      label="web.admin.content.friendlyProperty.labels.optionLabel"
                      value={option.displayText}
                      onChange={() => null}
                      showClear={false}
                      disabled
                      placeholder={t('web.admin.content.friendlyProperty.placeholder.optionLabel')}
                      fieldName={`detail-${config.id}-opt-${index}-display`}
                      ariaLabel={t('web.admin.content.friendlyProperty.labels.optionLabel')}
                    />
                  </div>
                  <div className={modalStyles.optionInputGroup}>
                    <Input
                      className={modalStyles.optionInput}
                      label="web.admin.content.friendlyProperty.labels.optionValue"
                      value={option.value}
                      onChange={() => null}
                      showClear={false}
                      disabled
                      placeholder={t('web.admin.content.friendlyProperty.placeholder.optionValue')}
                      fieldName={`detail-${config.id}-opt-${index}-value`}
                      ariaLabel={t('web.admin.content.friendlyProperty.labels.optionValue')}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </fieldset>
        )}

        <Flex gap={2} justify="end" className={modalStyles.footerActions}>
          <Button variant="secondary" onClick={onCancel} testId="custom-field-detail-cancel">
            {t('web.admin.visitorManagement.customField.detail.cancel')}
          </Button>
        </Flex>
      </div>

      <div className={modalStyles.preview} data-test="custom-field-detail-preview">
        <fieldset className={modalStyles.field}>
          <Label h1 TooltipComponent={t('web.admin.visitorManagement.customField.form.preview.tooltip')}>
            {t('web.admin.content.friendlyProperty.labels.preview')}
          </Label>
        </fieldset>
        <CustomFieldRenderer
          field={previewField}
          disabled={false}
          value={previewValue}
          onChange={setPreviewValue}
        />
      </div>
    </div>
  );
}
